import { ChevronRight } from '@mui/icons-material';
import { Box, Chip, ChipProps, Stack, StackProps, Typography } from '@mui/material';
import { reverse, sortBy } from 'lodash';
import React, { useMemo } from 'react';
import { useDemoForm } from '../../../../context/DemoFormContext';

export interface ConceptsCellProps extends StackProps {
  values?: Record<string, number>;
  isExclusion?: boolean;
  /**
   * Controlled expand state. When `undefined` (default) the cell renders
   * every chip and lets the desktop CSS (`.scores.shrink`) handle the
   * hide/show — that path keys off the surrounding DataGrid cell click.
   * When a boolean is passed the cell renders JS-controlled: `false`
   * shows just the first chip + an "{n} more" overflow, `true` shows
   * every chip without the overflow.
   */
  expanded?: boolean;
  /**
   * Optional click handler attached to the "{n} more" overflow chip.
   * Lets callers (e.g. the mobile card column) drive the toggle directly
   * since tap events on the chip don't always bubble to the surrounding
   * DataGrid cell click.
   */
  onToggleExpanded?: () => void;
}

export const ConceptChip: React.FC<ChipProps> = ({ className, color = 'secondary', ...props }) => {
  return (
    <Chip
      color={color}
      className={`concept-chip${className ? ` ${className}` : ''}`}
      sx={(theme) => {
        return {
          height: 'fit-content',
          '& .MuiChip-label': {
            //   padding: theme.spacing(2),
          },
          '&.MuiChip-root, & .MuiTypography-root, & .MuiChip-label': {
            fontSize: theme.typography.subtitle2.fontSize,
            wordWrap: 'break-word',
            whiteSpace: 'break-spaces',
          },
        };
      }}
      {...props}
    />
  );
};

const ConceptsCell: React.FC<ConceptsCellProps> = ({
  values = {},
  isExclusion = false,
  expanded,
  onToggleExpanded,
  ...props
}) => {
  const { apiConceptsBySlug, threshold } = useDemoForm();
  const visibleThreshold = useMemo(() => threshold || 0.7, [threshold]);

  const sortedItems = useMemo(() => {
    const itemKeys = Object.keys(values);
    const unsortedItems: { key: string; value: number }[] = [];
    itemKeys.forEach((key) => {
      const value = values[key];
      if (value >= visibleThreshold) {
        unsortedItems.push({
          key,
          value,
        });
      }
    });
    return reverse(sortBy(unsortedItems, 'value'));
  }, [values]);

  const itemCount = useMemo(() => {
    return sortedItems.length;
  }, [sortedItems]);

  // When `expanded` is undefined we leave visibility to the desktop CSS
  // (`.scores.shrink` toggles around the surrounding cell click). When
  // it's a boolean we render only what should be visible directly so the
  // mobile card layout doesn't need that CSS class.
  const visibleItems =
    expanded === undefined ? sortedItems : expanded ? sortedItems : sortedItems.slice(0, 1);
  // The overflow chip is hidden once expanded under the JS-controlled
  // path. Under the CSS-controlled path the desktop StyledDataGrid rules
  // hide it for the expanded state already.
  const showOverflow = expanded === false ? itemCount > 1 : expanded === undefined && itemCount > 1;

  return (
    <Stack
      gap={1}
      direction={'row'}
      flexWrap={'wrap'}
      overflow={'hidden'}
      textOverflow={'unset'}
      height={'100%'}
      justifyContent={'flex-start'}
      alignContent={'flex-start'}
      py={1}
      boxSizing={'border-box'}
      {...props}
    >
      {visibleItems.map(({ key, value }) => {
        return (
          <ConceptChip
            key={key}
            id={key}
            color={isExclusion ? 'error' : 'secondary'}
            label={
              <>
                {apiConceptsBySlug[key].title}:{' '}
                <Typography color="inherit" component={'span'} fontWeight={'bold'}>
                  {(value * 100).toFixed(0)}
                </Typography>
              </>
            }
          />
        );
      })}
      {showOverflow && (
        <ConceptChip
          id={'overflow-chip'}
          color={isExclusion ? 'error' : 'secondary'}
          // Same shape as the country-popularity overflow chip: don't
          // pass `sx` here (props.sx would override ConceptChip's own sx
          // and inflate the chip to MUI Chip's clickable 32px default).
          onClick={
            onToggleExpanded
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onToggleExpanded();
                }
              : undefined
          }
          label={
            <Box display="flex" justifyContent={'center'} alignItems={'center'}>
              {itemCount - 1} more <ChevronRight fontSize="inherit" />
            </Box>
          }
        />
      )}
    </Stack>
  );
};

export default ConceptsCell;
